import React, { useEffect, useState } from "react";
import notificationsService from "../services/notificationsService";
import { X } from "lucide-react";
import { markNotificationRead, getNotifications } from "../ApiHelper/url_Helpers";

const NotificationItem = ({ item }) => {
  const isRead = Boolean(item?.is_read ?? item?.read);
  const title = item?.title || "Notification";
  const msg = item?.message || item?.body || item?.data?.description || "";
  const createdAt = item?.created_at || item?.createdAt || new Date().toISOString();

  return (
    <div
      className={`relative p-6 mb-3 bg-white transition-shadow hover:shadow-md rounded-xl cursor-pointer overflow-hidden ${
        isRead ? "border border-gray-100" : "border-l-4 border-l-orange-400 border border-orange-100"
      }`}
    >
      <div className="flex items-start gap-3">
        {!isRead && <div className="mt-1 h-3 w-3 rounded-full bg-orange-400 flex-shrink-0" />}

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-800">{title}</div>
            <div className="text-xs text-gray-400 ml-3">{new Date(createdAt).toLocaleString()}</div>
          </div>
          <div className="text-xs text-gray-600 mt-2">{msg}</div>
        </div>
      </div>
    </div>
  );
};

export const NotificationsOffcanvas = ({ open, onClose }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(notificationsService.getNotifications());

    const handler = () => setItems(notificationsService.getNotifications());
    const newHandler = () => setItems(notificationsService.getNotifications());

    window.addEventListener("notificationsUpdated", handler);
    window.addEventListener("newNotification", newHandler);

    return () => {
      window.removeEventListener("notificationsUpdated", handler);
      window.removeEventListener("newNotification", newHandler);
    };
  }, []);

  const fetchServer = async () => {
    try {
      const resp = await getNotifications();
      const list = Array.isArray(resp?.notifications) ? resp.notifications : [];
      const normalizedItems = notificationsService.replaceNotifications(list);
      setItems(normalizedItems);
      notificationsService.setUnreadCount(resp?.unread_count ?? normalizedItems.filter((item) => !item.read).length);
    } catch (e) {
      console.warn("Failed to fetch server notifications", e);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchServer();
  }, [open]);

  const handleItemClick = async (item) => {
    const serverId = item?.id ?? item?.data?.id ?? item?.data?.notification_id ?? item?.data?.notificationId;

    if (!serverId) {
      await fetchServer();
      return;
    }

    try {
      await markNotificationRead(serverId);
      await fetchServer();
    } catch (e) {
      console.warn("Failed to mark notification read", e);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-full md:w-96 bg-white shadow-xl transform transition-transform z-50 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold">Notifications</h3>
          <button onClick={onClose} className="p-1 rounded-md border bg-gray-100">
            <X size={16} />
          </button>
        </div>

        <div className="p-2 overflow-y-auto h-[calc(100%-64px)]">
          {items.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No notifications</div>
          ) : (
            items.map((item) => (
              <div key={item.id} onClick={() => handleItemClick(item)}>
                <NotificationItem item={item} />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationsOffcanvas;
